import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { syncedLocationKeys } from './synced-locations';
import type { PartnerAccount, CreatePartnerAccountRequest, UpdatePartnerAccountRequest } from '@/types/api';

export const partnerAccountKeys = {
  all: ['partner-accounts'] as const,
  list: (includeCounts?: boolean) => [...partnerAccountKeys.all, 'list', { includeCounts }] as const,
  detail: (id: string) => [...partnerAccountKeys.all, 'detail', id] as const,
};

const POLLING_INTERVAL_MS = 3000;

export function usePartnerAccounts(options?: { includeCounts?: boolean; refetchInterval?: number | false }) {
  const includeCounts = options?.includeCounts ?? false;
  const refetchInterval = options?.refetchInterval ?? false;
  const url = includeCounts
    ? '/api/v1/partner-accounts?includeCounts=true'
    : '/api/v1/partner-accounts';

  return useQuery({
    queryKey: partnerAccountKeys.list(includeCounts),
    queryFn: () => api.get<PartnerAccount[]>(url),
    refetchInterval,
  });
}

/**
 * Fetches partner accounts with automatic polling when any account is syncing.
 * Polling stops automatically when no accounts are actively syncing.
 */
export function usePartnerAccountsWithPolling(options?: { includeCounts?: boolean }) {
  const includeCounts = options?.includeCounts ?? false;
  const url = includeCounts
    ? '/api/v1/partner-accounts?includeCounts=true'
    : '/api/v1/partner-accounts';

  return useQuery({
    queryKey: partnerAccountKeys.list(includeCounts),
    queryFn: () => api.get<PartnerAccount[]>(url),
    refetchInterval: (query) => {
      // Only poll when at least one account is actively syncing
      const accounts = query.state.data;
      const hasActiveSyncs = accounts?.some(account => account.syncStatus === 'syncing') ?? false;
      return hasActiveSyncs ? POLLING_INTERVAL_MS : false;
    },
  });
}

export function usePartnerAccount(id: string, options?: { includeStats?: boolean; refetchInterval?: number | false }) {
  const includeStats = options?.includeStats ?? true; // Default to true for detail views
  const refetchInterval = options?.refetchInterval ?? false;
  const url = includeStats
    ? `/api/v1/partner-accounts/${id}?includeStats=true`
    : `/api/v1/partner-accounts/${id}`;

  return useQuery({
    queryKey: [...partnerAccountKeys.detail(id), { includeStats }],
    queryFn: () => api.get<PartnerAccount>(url),
    enabled: !!id,
    refetchInterval,
  });
}

/**
 * Fetches a partner account with automatic polling when it's syncing.
 * Polling stops automatically when sync completes.
 */
export function usePartnerAccountWithPolling(id: string, options?: { includeStats?: boolean }) {
  const includeStats = options?.includeStats ?? true;
  const url = includeStats
    ? `/api/v1/partner-accounts/${id}?includeStats=true`
    : `/api/v1/partner-accounts/${id}`;

  return useQuery({
    queryKey: [...partnerAccountKeys.detail(id), { includeStats }],
    queryFn: () => api.get<PartnerAccount>(url),
    enabled: !!id,
    refetchInterval: (query) => {
      // Only poll when account is actively syncing
      const account = query.state.data;
      return account?.syncStatus === 'syncing' ? POLLING_INTERVAL_MS : false;
    },
  });
}

export function useCreatePartnerAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePartnerAccountRequest) =>
      api.post<PartnerAccount>('/api/v1/partner-accounts', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
    },
  });
}

export function useUpdatePartnerAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePartnerAccountRequest }) =>
      api.patch<PartnerAccount>(`/api/v1/partner-accounts/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
    },
  });
}

interface DisconnectOptions {
  handlePublishedLocations: 'unpublish_all' | 'keep_published';
}

export function useDisconnectPartnerAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, options }: { id: string; options: DisconnectOptions }) =>
      api.deleteWithBody<{ success: boolean }>(`/api/v1/partner-accounts/${id}`, options),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
    },
  });
}

// Sync control mutations
interface SyncResponse {
  success: boolean;
  jobId?: string;
}

export function useStartSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<SyncResponse>(`/api/v1/partner-accounts/${id}/sync/start`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: partnerAccountKeys.all,
        refetchType: 'all',
      });
    },
  });
}

export function usePauseSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<SyncResponse>(`/api/v1/partner-accounts/${id}/sync/pause`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: partnerAccountKeys.all,
        refetchType: 'all',
      });
    },
  });
}

export function useResumeSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<SyncResponse>(`/api/v1/partner-accounts/${id}/sync/resume`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: partnerAccountKeys.all,
        refetchType: 'all',
      });
    },
  });
}

// Enable/Disable mutations
interface EnableResponse {
  success: boolean;
}

/**
 * POST /partner-accounts/:id/disable.
 * - `queued` (HTTP 202): an `account_disable` batch operation unpublishes the
 *   `totalLocations` published locations; follow `operationId` with the
 *   batch-operation progress hooks. The account is disabled when it finishes,
 *   and only if nothing is left published.
 * - `completed` (HTTP 200): nothing was published; the account is disabled.
 */
export type DisableResponse =
  | { success: true; status: 'queued'; operationId: string; totalLocations: number }
  | { success: true; status: 'completed'; operationId: string | null; totalLocations: 0 };

export function useEnableAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<EnableResponse>(`/api/v1/partner-accounts/${id}/enable`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
    },
  });
}

export function useDisableAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<DisableResponse>(`/api/v1/partner-accounts/${id}/disable`),
    // On settled, not on success: the account and location lists are refetched
    // whatever the answer. A queued disable changes them again when its batch
    // operation finishes; EnableDisableToggle refetches then too.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}

// Reconnect mutation
interface ReconnectRequest {
  credentials: Record<string, string>;
}

export function useReconnectAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReconnectRequest }) =>
      api.post<{ success: boolean }>(`/api/v1/partner-accounts/${id}/reconnect`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
    },
  });
}

// Recalculate Prices mutation
interface RecalculatePricesResponse {
  success: boolean;
  locationsUpdated: number;
  publishedLocationsUpdated: number;
}

export function useRecalculatePrices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<RecalculatePricesResponse>(`/api/v1/partner-accounts/${id}/recalculate-prices`),
    onSuccess: () => {
      // Invalidate both partner accounts and synced locations queries
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['synced-locations'] });
    },
  });
}
